import {
  Component, 
  ElementRef,
  HostListener, inject, Injector,
  OnInit, viewChild,
} from '@angular/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { createSelector, select, Store } from "@ngrx/store";
import { AppState } from "../store";
import {
  arePricingPlansReady,
  isLoggedIn,
  isLoggedOut,
  isTeamManager,
  selectTenantInfo,
  selectUser,
  selectUserPermissions,
  userPictureUrl,
} from "../store/selectors";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { environment } from "../../environments/environment";
import { TenantService } from "../services/tenant.service";
import {
  checkIfPlatformSite,
  checkIfSingleSignOnPage, 
  goToLoginPage,
} from "../common/platform-utils";
import { isAnonymousUser, User } from "../models/user.model";
import { MatDialog } from "@angular/material/dialog";
import { NewsletterDialogComponent } from "../newsletter-dialog/newsletter-dialog.component";
import { UiEventsService } from "../services/ui-events.service";
import { MenuEntry, TenantInfo } from "../models/tenant.model";
import { createDefaultDialogConfig } from "../common/dialog-utils";
import { LoadingService } from "../shared/services/loading.service";
import { ResponsiveLayoutService } from "../services/responsive-layout.service";
import { TranslateModule } from "@ngx-translate/core";
import { AllowRoleDirective } from "../shared/auth/allow-role.directive";
import { AllowPlanDirective } from "../shared/allow-plan/allow-plan.directive";
import { DenyRoleDirective } from "../shared/auth/deny-role.directive";
import { AsyncPipe, NgClass, NgIf, NgStyle } from "@angular/common";
import { AiGlobalChatService } from "../ai-global-chat/ai-global-chat.service";
import { UrlBuilderService } from "../services/url-builder.service";
import { MessagesService } from "../shared/services/messages.service";
import { TrialExpirationComponent } from "../shared/trial-expiration/trial-expiration.component";
import { Auth } from "@angular/fire/auth";
import { UserPermissions } from "../models/user-permissions.model";
import { TopMenuSvgSpriteComponent } from "./top-menu-svg-sprite.component";
import { NotificationDialogComponent } from "./notification-dialog/notification-dialog.component";

import { UserNotificationsService } from "../services/user-notifications.service";
import { userDetailsUpdated } from "../store/user.actions";

import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { SitePagesService } from "../page-builder/site-pages/services/site-pages.service";
import { CustomPage } from "../models/page-builder/custom-page.model";
import { isLifetimeCustomer } from '../common/appsumo-utils';
import { createInstructorPlanDialog } from '../admin/school-settings/change-instructor-plan-dialog/change-instructor-plan-dialog.component';
import { MatTooltipModule } from "@angular/material/tooltip";
import { toObservable } from '@angular/core/rxjs-interop';
import { concatMap, filter, tap } from 'rxjs/operators';
import { InstructorPlanService } from '../admin/school-settings/instructor-plan.service';
import { GlobalAiIconSvgComponent } from '../svg-sprites/general/global-ai-icon-svg.component';
import { NgZone } from '@angular/core';
import { UserActivityLogService } from '../services/user-activity-log.service';
import { FeatureFlagService } from '../shared/services/feature-flag.service';
import { CookieService } from 'ngx-cookie-service';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
import { CloseIconSvgComponent } from '../svg-sprites/general/close-icon-svg.component';
interface TopMenuData {
  tenant: TenantInfo;
  isLoggedIn: boolean;
  isLoggedOut: boolean;
  pictureUrl: string;
  arePricingPlansReady: boolean;
  user: User;
  showSubscription: boolean;
  isTeamManager: boolean;
  permissions: UserPermissions;
}

declare const Stripe;

@Component({
  selector: "top-menu",
  templateUrl: "./top-menu.component.html",
  styleUrls: ["./top-menu.component.scss"],
  imports: [
    NgIf,
    NgClass,
    RouterLink,
    RouterLinkActive,
    DenyRoleDirective,
    AllowPlanDirective,
    AllowRoleDirective,
    AsyncPipe,
    TranslateModule,
    TrialExpirationComponent,
    TopMenuSvgSpriteComponent,
    NotificationDialogComponent,
    NgStyle,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    GlobalAiIconSvgComponent,
    CloseIconSvgComponent,
  ]
})
export class TopMenuComponent implements OnInit {
  data$: Observable<TopMenuData>;

  isPlatformSite: boolean;
  isSingleSignOnPage: boolean;
  isLandingPageMode = false;

  isFullScreenMode = false;
  isPhonePortrait: boolean;

  environment: any;

  isTenantLoginPage = false;

  isOpen: boolean = false;
  isOpenNotification: boolean = false;
  customHomePageAvailable: CustomPage;
  private homePageSubscription: Subscription;

  navBarLeftMenu = viewChild("navBarLeftMenu", {
    read: ElementRef
  });

  navBarRightMenu = viewChild("navBarRightMenu", {
    read: ElementRef
  });

  navBarContainer = viewChild('navBarContainer', { read: ElementRef });


  @HostListener("window:popstate", ["$event"])
  onPopState(event) {
    console.log("Back button pressed");
    this.aiChatService.endGlobalAiChatSession();
  }

  @HostListener("document:click", ["$event"])
  clickout(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpenNotification = false;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.scheduleRecalculation();
  }




  private resizeObserver: ResizeObserver;


  firstExtraMenuEntries: MenuEntry[] = [];

  moreMenuEntries: MenuEntry[] = [];

  injector = inject(Injector);

  instructorPlanService = inject(InstructorPlanService);

  private tenantInfo: TenantInfo;


  constructor(
    private store: Store<AppState>,
    private afAuth: Auth,
    private router: Router,
    private loading: LoadingService,
    private messages: MessagesService,
    private tenant: TenantService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private uiEvents: UiEventsService,
    private aiChatService: AiGlobalChatService,
    private responsive: ResponsiveLayoutService,
    private ub: UrlBuilderService,
    private elementRef: ElementRef,
    private userNotificationsService: UserNotificationsService,
    private sitePagesService: SitePagesService,
    private zone: NgZone,
    private userActivityLogService: UserActivityLogService,
    public featureFlagService: FeatureFlagService,
    private cookies: CookieService,
    private userPermissions: UserPermissionsService,
  ) {
    this.isPlatformSite = checkIfPlatformSite();
    this.isSingleSignOnPage = checkIfSingleSignOnPage();
    this.environment = environment;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isLandingPageMode =
          (window.location.search.includes("couponCode") ||
            window.location.search.includes("bundleCode")) &&
          !window.location.search.includes("purchaseResult");
      });

    this.store.select(selectTenantInfo).subscribe(tenant => {
      this.tenantInfo = tenant;
      this.recalculateMenuEntries(); // recalc if tenant changes
    });
  }

  async ngOnInit() {

    // console.log(`Top menu logo initialized.`, this.data.tenant.topMenuLogoConfig.topMenuLogoUrl);

    this.homePageSubscription = this.sitePagesService.homePage$.subscribe(
      (customPage) => {
        this.customHomePageAvailable = customPage;
      }
    );

    await this.sitePagesService.getHomePage();

    const selectTopMenuData = createSelector(
      selectTenantInfo,
      isLoggedIn,
      isLoggedOut,
      userPictureUrl,
      arePricingPlansReady,
      selectUser,
      isTeamManager,
      selectUserPermissions,
      (
        tenant,
        isLoggedIn,
        isLoggedOut,
        pictureUrl,
        arePricingPlansReady,
        user,
        isTeamManager,
        permissions
      ) => {

        return {
          tenant,
          isLoggedIn,
          isLoggedOut,
          pictureUrl,
          arePricingPlansReady,
          user,
          isTeamManager,
          showSubscription:
            permissions.isAdmin ||
            (arePricingPlansReady && tenant?.isConnectedToStripe),
          permissions,
        };
      }
    );

    this.data$ = this.store.pipe(select(selectTopMenuData));

    combineLatest([
      this.store.pipe(select(selectTenantInfo)),
      toObservable(this.navBarLeftMenu, { injector: this.injector }),
      toObservable(this.navBarRightMenu, { injector: this.injector }),
    ])
      .pipe(
        filter(([tenant, navBarLeftMenu, navBarRightMenu]) => navBarLeftMenu?.nativeElement && navBarRightMenu?.nativeElement),
      )
      .subscribe(([tenant]) => {
        const allExtraMenuEntries = tenant?.customMenu ?? [];

        const navBarMenuLeft = this.navBarLeftMenu?.()?.nativeElement;
        const navBarMenuRight = this.navBarRightMenu?.()?.nativeElement;

        if (!navBarMenuLeft || !navBarMenuRight) return;

        this.layoutMenuEntries(navBarMenuLeft, navBarMenuRight, allExtraMenuEntries);
      });




    this.uiEvents.fullScreen$.subscribe(
      (fullScreenMode) => (this.isFullScreenMode = fullScreenMode)
    );

    this.responsive.isMobilePortait$.subscribe(
      (isPhonePortrait) => (this.isPhonePortrait = isPhonePortrait)
    );

    this.isTenantLoginPage = document.location.pathname.endsWith("login-page");


    this.checkPurchaseSessionCookie();

  }


  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleRecalculation();
    });

    const containerEl = this.navBarContainer?.()?.nativeElement;
    const leftEl = this.navBarLeftMenu?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;


    if (containerEl) this.resizeObserver.observe(containerEl);
    if (leftEl) this.resizeObserver.observe(leftEl);
    if (rightEl) this.resizeObserver.observe(rightEl);

    requestAnimationFrame(() => this.recalculateMenuEntries());
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleRecalculation();
      });

    });

  }


  private resizeTimeout: any;

  // Use a more stable way to ensure view is ready
  private scheduleRecalculation() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Wait for DOM repaint to ensure getBoundingClientRect is accurate
      requestAnimationFrame(() => {
        // Wait 2 frames for safe measure
        requestAnimationFrame(() => {
          this.recalculateMenuEntries();
        });
      });
    }, 50);
  }



  ngOnDestroy(): void {
    this.homePageSubscription?.unsubscribe();

    const containerEl = this.navBarContainer?.()?.nativeElement;
    const leftEl = this.navBarLeftMenu?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;

    if (this.resizeObserver) {
      if (containerEl) this.resizeObserver.unobserve(containerEl);
      if (leftEl) this.resizeObserver.unobserve(leftEl);
      if (rightEl) this.resizeObserver.unobserve(rightEl);
    }
  }





  private layoutMenuEntries(leftEl: HTMLElement, rightEl: HTMLElement, menuEntries: MenuEntry[]) {
    const firstExtraMenuEntries: MenuEntry[] = [];
    const moreMenuEntries: MenuEntry[] = [];

    const leftEdge = leftEl.getBoundingClientRect().right;
    const rightEdge = rightEl.getBoundingClientRect().left;

    let widthAvailable = rightEdge - leftEdge - 50;

    for (const menuEntry of menuEntries) {
      const menuEntryWidth = (menuEntry?.title?.length ?? 10) * 8 + 40;
      if (widthAvailable > menuEntryWidth) {
        firstExtraMenuEntries.push(menuEntry);
        widthAvailable -= menuEntryWidth;
      } else {
        moreMenuEntries.push(menuEntry);
      }
    }

    this.firstExtraMenuEntries = firstExtraMenuEntries;
    this.moreMenuEntries = moreMenuEntries;
  }


  private recalculateMenuEntries() {
    const tenant = this.tenantInfo;
    const allExtraMenuEntries = tenant?.customMenu ?? [];

    const leftEl = this.navBarLeftMenu?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;

    if (!leftEl || !rightEl) return;

    this.layoutMenuEntries(leftEl, rightEl, allExtraMenuEntries);
  }




  toggleMenu() {
    this.isOpen = !this.isOpen;
  }

  login(startInCoursesPage = false, tenantInfo: TenantInfo) {
    if (document.location.pathname?.endsWith("login-page")) {
      return;
    }
    goToLoginPage(this.router, startInCoursesPage, this.tenant.id);
  }

  async logout(tenantInfo: TenantInfo, userId: string) {

    await this.userActivityLogService.reportUserLogout(userId, this.tenant.id);

    this.afAuth.signOut().then(() => {
      this.loading.loadingOn();

      window.location.href = "/courses";
    });
  }

  async toggleNotificationDialog(
    userId: string,
    unreadNotificationsCounter: number
  ) {
    try {
      this.isOpenNotification = !this.isOpenNotification;

      if (this.isOpenNotification === true && unreadNotificationsCounter > 0) {
        await this.userNotificationsService.zeroOutNotificationsCounter(userId);

        this.store.dispatch(
          userDetailsUpdated({
            userId,
            changes: { unreadNotificationsCounter: 0 },
          })
        );
      }
    } catch (e) {
      this.isOpenNotification = !this.isOpenNotification;
      this.messages.error(e);
    }
  }

  showNewsletterPopup() {
    const dialogConfig = createDefaultDialogConfig();

    dialogConfig.width = "710px";

    this.dialog.open(NewsletterDialogComponent, dialogConfig);
  }

  onMenuClicked() {
    if (!window.location.hostname.startsWith("login")) {
    }
  }

  joinPartnerProgram() {
    window.open(
      "https://instructor-academy.onlinecoursehost.com/partner-program/",
      "_blank"
    );
  }

  openHelpCenter() {
    window.open("https://help.onlinecoursehost.com", "_blank");
  }

  closeAiGlobalChat() {
    this.aiChatService.endGlobalAiChatSession();
  }

  toggleAiChat(event, tenant: TenantInfo) {
    if (tenant?.instructorPricingPlan == "growth") {
      this.messages.info(
        `The AI Course Creation Assistant is only available if you have a paid plan.`
      );
      return;
    }

    this.aiChatService.toggleAiChat();
    event.stopPropagation();
  }

  isAdminMenuAllowed(data: TopMenuData) {
    
    return (
      data.permissions.isAdmin ||
      data.permissions.isPlatformAdmin ||
      this.userPermissions.can('Admin') ||
      data.permissions.isCourseAdmin
    );
  }

  showOnBoardingTutorials(data: TopMenuData) {
    return  data.permissions.isPlatformAdmin || data.permissions.isAdmin;
  }

  isCommunityVisible(data: TopMenuData) {
    //console.log(`Checking community visibility: `, data);
    // check if the platform feature is on or not
    if (!environment?.features?.community) {
      //console.log(`Community feature is disabled.`);
      return false;
    }

    //check if user is banned from the community
    if (data.user?.communityMemberSettings?.banned) {
      return false;
    }
    // for the moment the community is only visible to platform admins
    if (data?.permissions?.isPlatformAdmin) {
      //console.log(`Community is visible to platform admins.`);
      return true;
    }
    // the community is visible to website admins and course admins
    if (data?.permissions?.isAdmin || data?.permissions?.isCourseAdmin) {
      //console.log(`Community is visible to website admins and course admins.`);
      return true;
    }
    // if it's a student user and the community is explicitly enabled
    if (
      !data?.permissions?.isAdmin &&
      !data?.permissions?.isCourseAdmin &&
      !(data?.tenant?.communitySettings?.disabled ?? true)
    ) {
      //console.log(`Community is visible to students.`);
      return true;
    }
    // if the community is free and the user is not logged in, allow access
    if (
      isAnonymousUser(data?.user) &&
      data?.tenant?.communitySettings?.communityAccess == "free" &&
      data?.tenant?.communitySettings?.disabled == false
    ) {
      return true;
    }
    // all other cases hide the community from the top menu
    //console.log(`Community is hidden.`);
    return false;
  }

  isPageBuilderVisible(data: TopMenuData) {
    const active = environment?.features?.pageBuilder || data?.permissions?.isPlatformAdmin;
    const hasPermission = this.userPermissions.can(PermissionKeys.PageBuilder);
    return active &&(data?.permissions?.isAdmin || hasPermission);
  }

  isUpgradeButtonVisible(data: TopMenuData) {
    // the button is only visible to logged-in users
    if (isAnonymousUser(data.user)) {
      return false;
    }
    // the upgrade button is only visible to admins
    if (!data.permissions.isAdmin) {
      return false;
    }
    const { instructorPricingPlan } = data.tenant;
    // there is no upgrade possible above the unlimited plan
    if (data.tenant?.instructorPricingPlan == "unlimited") {
      return false;
    }

    // there is no upgrade before activating a plan
    if (data.tenant?.instructorPricingPlan == "growth") {
      return false;
    }

    // for the moment, don't show the upgrade button to old lifetime plans, just to the new plan with 5GB limit
    //if (instructorPricingPlan == "lifetime" || instructorPricingPlan == "lifetime-pro") {
    //  return false;
    //}
    // check if the button is active on this environment
    return environment?.features?.upgradeButton;
  }

  async onUpgradeBtnClicked(data: TopMenuData) {
    if (isLifetimeCustomer(data.tenant)) {
      await this.router.navigateByUrl("/upgrade-to-unlimited");
    }
    else {
      createInstructorPlanDialog(this.dialog)
        .pipe(
          concatMap(plan => this.loading.showLoaderUntilCompleted(
            this.instructorPlanService.updateInstructorPlan(
              false,
              this.tenant.tenant['email'],
              plan?.selectedInstructorPlanId,
              plan?.selectedPlanFrequency
            )
          )
          ),
          tap(response => {

            const stripe = Stripe(response.stripePublicKey);

            stripe.redirectToCheckout({
              sessionId: response.sessionId
            });

          })
        )
        .subscribe();
    }
  }

  closeNotificationDialog() {
    this.isOpenNotification = false;
  }

  evaluateCustomMenuHref(customMenu: MenuEntry) {

    if (customMenu?.['dynamic']) {

      if (customMenu?.link) {
        const {
          pathname
        } = new URL(customMenu.link);

        const origin = location.origin

        if (pathname.split('/').filter(Boolean)[0] === 'pages') {

          return origin + pathname

        }

      }

    }

    return customMenu?.link

  }



  // isAnalyticsVisible(data: TopMenuData) {
  //   const active = environment?.features?.analytics || data?.permissions?.isPlatformAdmin;
  //   return active && data?.permissions?.isAdmin;
  // }

  checkPurchaseSessionCookie() {
    const purchaseSessionId = this.cookies.get("purchaseSessionId");

    if (purchaseSessionId) {
      const currentPath = window.location.pathname;
      this.data$.subscribe((data) => {
        if (data.isLoggedIn) {
          if (currentPath !== '/purchasecomplete') {
            window.location.href = "/purchasecomplete";

          }
        }
      });
    }
  }

  adminMenuLink(data: TopMenuData): string {
    if (data.permissions.isCourseAdmin) {
      return '/admin/settings/manage-students';
      
    }
    return this.userPermissions.redirectUserAccordingToPermissions();
  }
  

}
